console.log("VPX AVATAR FILE LOADED");

(function () {

    if (!window.VORYNEX) {
        console.error("VORYNEX namespace not found. Avatar module aborted.");
        return;
    }

    /*
    ==========================================
    Avatar Rendering System
    ------------------------------------------
    Fully custom, procedurally-drawn flat-vector
    mascot — deliberately abstract/geometric, not
    photorealistic. Two reasons: (1) it sidesteps
    any resemblance-to-a-real-person issue outright
    ("no creepy uncanny valley"), and (2) it's
    100% code-driven, so no artist/licensing
    dependency to ship v1. The data model below
    (avatarConfig shape, layer functions) is
    designed so a more detailed illustrated art
    style could be swapped in later WITHOUT
    changing anything that calls this module —
    only the internal path-drawing functions
    would need replacing.

    bodyStage (1-5) is NEVER stored — it's always
    computed live from the "progress" category in
    vpx-achievements.js, so the avatar always
    reflects current standing, never a stale save.
    ==========================================
    */

    const SKIN_TONES = {
        tone1: "#f4d0ae", tone2: "#e2b28a", tone3: "#c68a5f",
        tone4: "#9c6539", tone5: "#5c3a22"
    };

    const OUTFIT_TYPES = ["tshirt_shorts", "tank_shorts", "hoodie", "tracksuit", "leggings"];
    const HAIR_STYLES = ["short", "long", "bun", "bald", "curly", "ponytail"];
    const ACCESSORIES = ["none", "glasses", "cap", "headphones", "gymbag", "shaker"];

    /*
    ==========================================
    Gamified Unlocks
    ------------------------------------------
    Starter items (tshirt_shorts, no accessory)
    are always available. Everything else is tied
    to a specific achievement tier from
    vpx-achievements.js — spread across all 7
    categories on purpose, so working toward any
    achievement also unlocks visible avatar
    rewards, not just a hidden number going up.
    ==========================================
    */

    const UNLOCK_REQUIREMENTS = {
        outfitType: {
            tshirt_shorts: null,
            tank_shorts: { category: "streak", tier: "bronze" },
            leggings: { category: "progress", tier: "bronze" },
            hoodie: { category: "checkins", tier: "silver" },
            tracksuit: { category: "workouts", tier: "gold" }
        },
        accessory: {
            none: null,
            shaker: null,
            glasses: { category: "membership", tier: "bronze" },
            cap: { category: "streak", tier: "silver" },
            headphones: { category: "strengthPR", tier: "bronze" },
            gymbag: { category: "programs", tier: "bronze" }
        }
    };

    function isRequirementMet(requirement) {
        if (!requirement) return true; // no requirement = always unlocked
        if (!window.VORYNEX.achievements || !window.VORYNEX.achievements.evaluate) return true; // fail open if module missing
        const result = window.VORYNEX.achievements.evaluate();
        const cat = result.categories.filter(function (c) { return c.id === requirement.category; })[0];
        if (!cat) return true;
        return cat.tiersUnlocked.indexOf(requirement.tier) !== -1;
    }

    function clamp(v, min, max) {
        return Math.min(max, Math.max(min, v));
    }

    function getSection(name, fallback) {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return fallback;
        const val = window.VORYNEX.state.getSection(name);
        return (val === null || val === undefined) ? fallback : val;
    }

    /*
    ==========================================
    Body Stage — derived, not stored
    ------------------------------------------
    Reuses the "progress" category value from
    vpx-achievements.js (0-100% toward goal) to
    pick a body silhouette stage 1-5. Direction
    (leaner vs more built) follows the user's
    actual goal, so fat_loss and muscle_gain
    users see visually different evolution even
    at the same % complete.
    ==========================================
    */

    function getBodyStage() {
        const blueprint = getSection("blueprint", null);
        const goal = (blueprint && blueprint.goal && blueprint.goal.goal) || "fat_loss";

        let progressPct = 0;
        if (window.VORYNEX.achievements && window.VORYNEX.achievements.evaluate) {
            const evalResult = window.VORYNEX.achievements.evaluate();
            const progressCat = evalResult.categories.filter(function (c) { return c.id === "progress"; })[0];
            progressPct = progressCat ? progressCat.value : 0;
        }

        const stage = 1 + Math.floor(progressPct / 25); // 0-24%→1, 25-49%→2, 50-74%→3, 75-99%→4, 100%→5
        return { stage: clamp(stage, 1, 5), goal: goal };
    }

    /*
    ==========================================
    Layer Builders
    ------------------------------------------
    Each returns an SVG fragment string. Torso
    width/shape scales with bodyStage: fat_loss
    goals taper the waist at higher stages,
    muscle_gain goals widen the shoulders/taper
    the waist (V-shape) at higher stages.
    ==========================================
    */

    function torsoMetrics(stage, goal, gender) {
        const base = gender === "female" ? { shoulder: 34, waist: 30, hip: 36 } : { shoulder: 40, waist: 34, hip: 32 };

        if (goal === "muscle_gain") {
            base.shoulder += (stage - 1) * 3;
            base.waist -= (stage - 1) * 0.8;
        } else if (goal === "fat_loss") {
            base.waist -= (stage - 1) * 2.2;
            base.hip -= (stage - 1) * 1.2;
        } else {
            base.waist -= (stage - 1) * 0.5;
        }

        return base;
    }

    function buildBody(config, stage, goal) {
        const skin = SKIN_TONES[config.skinTone] || SKIN_TONES.tone3;
        const m = torsoMetrics(stage, goal, config.gender);
        const cx = 100;

        return `
            <ellipse cx="${cx - 20}" cy="205" rx="11" ry="42" fill="${skin}"/>
            <ellipse cx="${cx + 20}" cy="205" rx="11" ry="42" fill="${skin}"/>
            <path d="M ${cx - m.shoulder / 2} 110 Q ${cx - m.waist / 2 - 4} 150 ${cx - m.hip / 2} 178 L ${cx + m.hip / 2} 178 Q ${cx + m.waist / 2 + 4} 150 ${cx + m.shoulder / 2} 110 Z" fill="${skin}"/>
            <ellipse cx="${cx - m.shoulder / 2 - 8}" cy="130" rx="9" ry="30" fill="${skin}"/>
            <ellipse cx="${cx + m.shoulder / 2 + 8}" cy="130" rx="9" ry="30" fill="${skin}"/>
            <circle cx="${cx}" cy="72" r="30" fill="${skin}"/>
        `;
    }

    function buildOutfit(config, stage, goal) {
        const m = torsoMetrics(stage, goal, config.gender);
        const cx = 100;
        const color = config.outfitColor || "#c9a227";
        const type = config.outfitType || "tshirt_shorts";
        let svg = "";

        const topPath = `<path d="M ${cx - m.shoulder / 2} 108 Q ${cx - m.waist / 2 - 4} 145 ${cx - m.hip / 2 - 2} 165 L ${cx + m.hip / 2 + 2} 165 Q ${cx + m.waist / 2 + 4} 145 ${cx + m.shoulder / 2} 108 Z" fill="${color}"/>`;
        const sleeveShort = `<ellipse cx="${cx - m.shoulder / 2 - 6}" cy="118" rx="10" ry="14" fill="${color}"/><ellipse cx="${cx + m.shoulder / 2 + 6}" cy="118" rx="10" ry="14" fill="${color}"/>`;
        const sleeveLong = `<ellipse cx="${cx - m.shoulder / 2 - 8}" cy="130" rx="9.5" ry="31" fill="${color}"/><ellipse cx="${cx + m.shoulder / 2 + 8}" cy="130" rx="9.5" ry="31" fill="${color}"/>`;
        const shortsBottom = `<path d="M ${cx - m.hip / 2 - 2} 165 L ${cx + m.hip / 2 + 2} 165 L ${cx + m.hip / 2 - 4} 195 L ${cx + 4} 190 L ${cx} 195 L ${cx - m.hip / 2 + 4} 195 Z" fill="${color}"/>`;
        const fullLegsBottom = `<path d="M ${cx - m.hip / 2 - 2} 165 L ${cx + m.hip / 2 + 2} 165 L ${cx + 26} 244 L ${cx + 12} 244 L ${cx + 2} 190 L ${cx - 2} 190 L ${cx - 12} 244 L ${cx - 26} 244 Z" fill="${color}"/>`;

        if (type === "tank_shorts") {
            svg = topPath + shortsBottom;
        } else if (type === "tshirt_shorts") {
            svg = topPath + sleeveShort + shortsBottom;
        } else if (type === "hoodie") {
            svg = topPath + sleeveLong + fullLegsBottom + `<path d="M ${cx - 22} 95 Q ${cx} 78 ${cx + 22} 95 Q ${cx + 20} 108 ${cx} 112 Q ${cx - 20} 108 ${cx - 22} 95 Z" fill="${color}"/>`;
        } else if (type === "tracksuit") {
            svg = topPath + sleeveLong + fullLegsBottom;
        } else if (type === "leggings") {
            svg = topPath + sleeveShort + `<path d="M ${cx - m.hip / 2 - 2} 165 L ${cx + m.hip / 2 + 2} 165 L ${cx + 24} 244 L ${cx + 13} 244 L ${cx + 2} 190 L ${cx - 2} 190 L ${cx - 13} 244 L ${cx - 24} 244 Z" fill="${color}" opacity="0.92"/>`;
        }

        // Branding is always shown now — every avatar is a small piece
        // of free marketing, not an optional toggle.
        svg += `<text x="${cx}" y="130" text-anchor="middle" font-size="13" font-weight="bold" fill="#141414" opacity="0.55" font-family="sans-serif">V</text>`;

        return svg;
    }

    function buildHair(config) {
        const cx = 100, cy = 72;
        const color = config.hairColor || "#2b2118";
        const style = config.hairStyle || "short";

        if (style === "bald") return "";
        if (style === "short") return `<path d="M ${cx - 30} 70 Q ${cx - 32} 42 ${cx} 40 Q ${cx + 32} 42 ${cx + 30} 70 Q ${cx + 30} 55 ${cx} 52 Q ${cx - 30} 55 ${cx - 30} 70 Z" fill="${color}"/>`;
        if (style === "curly") return `<circle cx="${cx - 22}" cy="52" r="11" fill="${color}"/><circle cx="${cx}" cy="42" r="13" fill="${color}"/><circle cx="${cx + 22}" cy="52" r="11" fill="${color}"/>`;
        if (style === "long") return `<path d="M ${cx - 31} 70 Q ${cx - 34} 40 ${cx} 38 Q ${cx + 34} 40 ${cx + 31} 70 L ${cx + 27} 118 L ${cx + 19} 118 L ${cx + 22} 68 Q ${cx} 50 ${cx - 22} 68 L ${cx - 19} 118 L ${cx - 27} 118 Z" fill="${color}"/>`;
        if (style === "bun") return `<path d="M ${cx - 30} 70 Q ${cx - 32} 42 ${cx} 40 Q ${cx + 32} 42 ${cx + 30} 70 Q ${cx + 30} 55 ${cx} 52 Q ${cx - 30} 55 ${cx - 30} 70 Z" fill="${color}"/><circle cx="${cx}" cy="34" r="9" fill="${color}"/>`;
        if (style === "ponytail") return `<path d="M ${cx - 30} 70 Q ${cx - 32} 42 ${cx} 40 Q ${cx + 32} 42 ${cx + 30} 70 Q ${cx + 30} 55 ${cx} 52 Q ${cx - 30} 55 ${cx - 30} 70 Z" fill="${color}"/><path d="M ${cx + 28} 55 Q ${cx + 42} 65 ${cx + 34} 100 L ${cx + 27} 98 Q ${cx + 32} 68 ${cx + 22} 58 Z" fill="${color}"/>`;
        return "";
    }

    function buildAccessory(config) {
        const cx = 100, cy = 72;
        const acc = config.accessory || "none";

        if (acc === "glasses") return `<circle cx="${cx - 11}" cy="74" r="8" fill="none" stroke="#141414" stroke-width="2.5"/><circle cx="${cx + 11}" cy="74" r="8" fill="none" stroke="#141414" stroke-width="2.5"/><line x1="${cx - 3}" y1="74" x2="${cx + 3}" y2="74" stroke="#141414" stroke-width="2.5"/>`;
        if (acc === "cap") return `<path d="M ${cx - 31} 60 Q ${cx - 33} 34 ${cx} 32 Q ${cx + 33} 34 ${cx + 31} 60 Z" fill="#141414"/><ellipse cx="${cx + 24}" cy="58" rx="16" ry="5" fill="#141414"/>`;
        if (acc === "headphones") return `<path d="M ${cx - 32} 68 Q ${cx - 32} 40 ${cx} 40 Q ${cx + 32} 40 ${cx + 32} 68" fill="none" stroke="#141414" stroke-width="4"/><rect x="${cx - 36}" y="62" width="9" height="18" rx="4" fill="#141414"/><rect x="${cx + 27}" y="62" width="9" height="18" rx="4" fill="#141414"/>`;
        if (acc === "gymbag") return `<rect x="${cx + 34}" y="150" width="20" height="26" rx="4" fill="#3a3a3a"/><rect x="${cx + 38}" y="144" width="12" height="8" rx="3" fill="#3a3a3a"/>`;
        if (acc === "shaker") {
            const x = 130, y = 145;
            return `
                <rect x="${x}" y="${y}" width="20" height="38" rx="4" fill="#f5f5f0"/>
                <rect x="${x}" y="${y}" width="20" height="9" rx="4" fill="#141414"/>
                <rect x="${x + 2}" y="${y + 12}" width="16" height="3" fill="#141414"/>
                <text x="${x + 10}" y="${y + 27}" text-anchor="middle" font-size="10" font-weight="bold" fill="#141414" font-family="sans-serif">V</text>
            `;
        }
        return "";
    }

    /*
    ==========================================
    Public API
    ==========================================
    */

    const Avatar = {};

    Avatar.getConfig = function () {
        return getSection("avatarConfig", {
            gender: "male", skinTone: "tone3", hairStyle: "short", hairColor: "#2b2118",
            outfitType: "tshirt_shorts", outfitColor: "#c9a227", accessory: "none"
        });
    };

    Avatar.setConfig = function (partialConfig) {
        if (!window.VORYNEX.state || !window.VORYNEX.state.updateSection) return;
        window.VORYNEX.state.updateSection("avatarConfig", partialConfig);
    };

    Avatar.getOptions = function () {
        return {
            genders: ["male", "female"],
            skinTones: Object.keys(SKIN_TONES),
            hairStyles: HAIR_STYLES.slice(),
            outfitTypes: OUTFIT_TYPES.slice(),
            accessories: ACCESSORIES.slice()
        };
    };

    // Only outfitType and accessory are gated — gender/skin/hair are
    // always free self-expression choices, never locked behind
    // performance (locking identity-adjacent options behind achievements
    // would be a bad experience, not just a bad look).
    Avatar.isUnlocked = function (field, value) {
        const req = UNLOCK_REQUIREMENTS[field];
        if (!req) return true;
        return isRequirementMet(req[value]);
    };

    Avatar.getUnlockRequirement = function (field, value) {
        const req = UNLOCK_REQUIREMENTS[field];
        return (req && req[value]) ? req[value] : null;
    };

    Avatar.getBodyStage = function () {
        return getBodyStage();
    };

    // Renders a self-contained <svg> string, viewBox 0 0 200 260.
    // overrideConfig lets the customization screen preview choices
    // before saving.
    Avatar.render = function (overrideConfig) {
        const config = Object.assign({}, Avatar.getConfig(), overrideConfig || {});
        const stageInfo = getBodyStage();

        const body = buildBody(config, stageInfo.stage, stageInfo.goal);
        const outfit = buildOutfit(config, stageInfo.stage, stageInfo.goal);
        const hair = buildHair(config);
        const accessory = buildAccessory(config);

        return `<svg viewBox="0 0 200 260" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="VORYNEX Blueprint avatar">
            ${body}
            ${outfit}
            ${hair}
            ${accessory}
        </svg>`;
    };

    window.VORYNEX.avatar = Avatar;
    console.log("Avatar module exported. Body stage:", getBodyStage().stage, "/ 5");

})();